// src/pages/test/TestPublicAccessPage.jsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  TestTube, 
  Key, 
  Link as LinkIcon, 
  CheckCircle, 
  XCircle,
  ArrowRight,
  Copy,
  Eye,
  EyeOff
} from 'lucide-react';
import toast from 'react-hot-toast';

const TestPublicAccessPage = () => {
  const navigate = useNavigate();
  const [contractId, setContractId] = useState('');
  const [generatedAccess, setGeneratedAccess] = useState(null);
  const [showCode, setShowCode] = useState(false);
  const [testResults, setTestResults] = useState([]);

  const addTestResult = (step, success, message) => {
    setTestResults(prev => [...prev, { step, success, message, timestamp: new Date() }]);
  };

  const generateAccess = async () => {
    try {
      addTestResult('Generare', null, 'Se generează codul de acces...');
      
      const response = await fetch(`/api/contracts/${contractId}/generate-access`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify({ validityHours: 48 })
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      setGeneratedAccess(data);
      addTestResult('Generare', true, `Cod generat cu succes: ${data.accessCode}`);
      toast.success('Cod de acces generat!');
    } catch (error) {
      addTestResult('Generare', false, `Eroare: ${error.message}`);
      toast.error('Eroare la generarea codului');
      console.error(error);
    }
  };

  const testTokenValidation = async () => {
    if (!generatedAccess) return;

    try {
      addTestResult('Validare Token', null, 'Se validează token-ul...');
      
      const response = await fetch(`/api/contract-access/${generatedAccess.token}`);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      addTestResult('Validare Token', true, `Token valid. Contract: ${data.contractNumber}`);
      toast.success('Token validat cu succes!');
    } catch (error) {
      addTestResult('Validare Token', false, `Eroare: ${error.message}`);
      toast.error('Token invalid');
    }
  };

  const testCodeVerification = async () => {
    if (!generatedAccess) return;

    try {
      addTestResult('Verificare Cod', null, 'Se verifică codul de acces...');
      
      const response = await fetch(`/api/contract-access/${generatedAccess.token}/verify-code`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ Code: generatedAccess.accessCode })
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      addTestResult('Verificare Cod', true, 'Cod verificat cu succes!');
      toast.success('Cod verificat cu succes!');
    } catch (error) {
      addTestResult('Verificare Cod', false, `Eroare: ${error.message}`);
      toast.error('Cod invalid');
    }
  };

  const testFullFlow = async () => {
    setTestResults([]);
    await generateAccess();
    
    // Așteaptă 1 secundă între teste
    setTimeout(async () => {
      await testTokenValidation();
      setTimeout(async () => {
        await testCodeVerification();
      }, 1000);
    }, 1000);
  };

  const openAccessPage = () => {
    if (!generatedAccess) return;
    window.open(`/contract-access/${generatedAccess.token}`, '_blank');
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copiat în clipboard!');
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex items-center mb-4">
            <TestTube className="w-8 h-8 text-blue-600 mr-3" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Test Public Access Flow
              </h1>
              <p className="text-sm text-gray-600">
                Testați funcționalitatea de acces public pentru contracte
              </p>
            </div>
          </div>

          {/* Input */}
          <div className="flex gap-3">
            <input
              type="number"
              value={contractId}
              onChange={(e) => setContractId(e.target.value)}
              placeholder="ID Contract (ex: 1)"
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <button
              onClick={testFullFlow}
              disabled={!contractId}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors inline-flex items-center"
            >
              <TestTube className="w-5 h-5 mr-2" />
              Testează Flow Complet
            </button>
          </div>
        </div>

        {/* Generated Access Info */}
        {generatedAccess && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Key className="w-5 h-5 mr-2 text-green-600" />
              Informații Acces Generat
            </h2>

            <div className="space-y-4">
              {/* Access Code */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Cod de Acces
                </label>
                <div className="flex items-center gap-2">
                  <div className="flex-1 bg-gray-50 border border-gray-300 rounded-lg px-4 py-3 font-mono text-xl tracking-widest text-center">
                    {showCode ? generatedAccess.accessCode : '••••••'}
                  </div>
                  <button
                    onClick={() => setShowCode(!showCode)}
                    className="p-3 text-gray-600 hover:text-gray-900 rounded-lg hover:bg-gray-100"
                  >
                    {showCode ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                  <button
                    onClick={() => copyToClipboard(generatedAccess.accessCode)}
                    className="p-3 text-blue-600 hover:text-blue-700 rounded-lg hover:bg-blue-50"
                  >
                    <Copy className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Access URL */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Link de Acces
                </label>
                <div className="flex items-center gap-2">
                  <div className="flex-1 bg-gray-50 border border-gray-300 rounded-lg px-4 py-3 text-sm text-gray-700 truncate">
                    {generatedAccess.accessUrl}
                  </div>
                  <button
                    onClick={() => copyToClipboard(generatedAccess.accessUrl)}
                    className="p-3 text-blue-600 hover:text-blue-700 rounded-lg hover:bg-blue-50"
                  >
                    <Copy className="w-5 h-5" />
                  </button>
                  <button
                    onClick={openAccessPage}
                    className="p-3 text-green-600 hover:text-green-700 rounded-lg hover:bg-green-50"
                  >
                    <ArrowRight className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Token */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Token
                </label>
                <div className="bg-gray-50 border border-gray-300 rounded-lg px-4 py-3 text-xs text-gray-600 font-mono truncate">
                  {generatedAccess.token}
                </div>
              </div>

              {/* Expiry Info */}
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">Expiră la:</span>
                <span className="font-medium text-gray-900">
                  {new Date(generatedAccess.expiresAt).toLocaleString('ro-RO')}
                </span>
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-4 border-t">
                <button
                  onClick={testTokenValidation}
                  className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Test Validare Token
                </button>
                <button
                  onClick={testCodeVerification}
                  className="flex-1 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
                >
                  Test Verificare Cod
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Test Results */}
        {testResults.length > 0 && (
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Rezultate Teste
            </h2>
            <div className="space-y-2">
              {testResults.map((result, index) => (
                <div
                  key={index}
                  className={`flex items-start p-3 rounded-lg ${
                    result.success === null
                      ? 'bg-blue-50 border border-blue-200'
                      : result.success
                      ? 'bg-green-50 border border-green-200'
                      : 'bg-red-50 border border-red-200'
                  }`}
                >
                  <div className="mr-3 mt-0.5">
                    {result.success === null ? (
                      <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                    ) : result.success ? (
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    ) : (
                      <XCircle className="w-5 h-5 text-red-600" />
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <span className={`font-medium ${
                        result.success === null
                          ? 'text-blue-900'
                          : result.success
                          ? 'text-green-900'
                          : 'text-red-900'
                      }`}>
                        {result.step}
                      </span>
                      <span className="text-xs text-gray-500">
                        {result.timestamp.toLocaleTimeString('ro-RO')}
                      </span>
                    </div>
                    <p className={`text-sm mt-1 ${
                      result.success === null
                        ? 'text-blue-700'
                        : result.success
                        ? 'text-green-700'
                        : 'text-red-700'
                    }`}>
                      {result.message}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Instructions */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mt-6">
          <h3 className="font-semibold text-blue-900 mb-3">
            Cum să testezi:
          </h3>
          <ol className="text-sm text-blue-800 space-y-2">
            <li>1. Introdu ID-ul unui contract existent</li>
            <li>2. Apasă "Testează Flow Complet" pentru a rula toate testele</li>
            <li>3. Verifică rezultatele testelor mai jos</li>
            <li>4. Sau folosește butoanele individuale pentru a testa fiecare pas separat</li>
            <li>5. Deschide link-ul de acces într-un tab nou pentru a testa interfața de utilizator</li>
          </ol>
        </div>
      </div>
    </div>
  );
};

export default TestPublicAccessPage;